import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaLock,
  FaEye,
  FaDatabase,
  FaUserShield,
  FaCookie,
  FaEnvelope,
} from "react-icons/fa";
import { PHONENUMBER, CAPITAL, COUNTRY } from "../config/appConfig";

const PrivacyPolicy = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const sections = [
    {
      title: "Information We Collect",
      icon: FaDatabase,
      content: `We collect information you provide directly to us, including:
        • Phone number for account registration and verification
        • Transaction history for deposits and withdrawals
        • Game activity and betting history
        • Device information and IP address for security purposes
        • Any communications you send to our support team`,
    },
    {
      title: "How We Use Your Information",
      icon: FaEye,
      content: `We use your information to:
        • Process your transactions and manage your account
        • Provide customer support and respond to inquiries
        • Prevent fraud and ensure platform security
        • Send important updates about your account and our services
        • Improve our platform and develop new features
        • Comply with legal and regulatory requirements`,
    },
    {
      title: "Information Security",
      icon: FaLock,
      content: `We implement robust security measures to protect your personal information:
        • Industry-standard encryption for all data transmission
        • Secure servers with regular security audits
        • Access controls limiting who can view your data
        • Regular monitoring for suspicious activities
        We cannot guarantee absolute security, but we continuously work to protect your information.`,
    },
    {
      title: "Data Sharing",
      icon: FaUserShield,
      content: `We do not sell your personal information. We may share data only:
        • With payment processors to facilitate transactions
        • With law enforcement when legally required
        • With service providers who assist our operations
        • To protect our rights and prevent fraud
        All third parties are bound by confidentiality agreements.`,
    },
    {
      title: "Cookies and Tracking",
      icon: FaCookie,
      content: `We use cookies and similar technologies to:
        • Remember your preferences and login status
        • Analyze platform usage and performance
        • Enhance security and prevent fraud
        You can manage cookie preferences in your browser settings, though some features may not function properly without cookies.`,
    },
    {
      title: "Your Rights",
      icon: FaUserShield,
      content: `You have the right to:
        • Access your personal information
        • Request correction of inaccurate data
        • Request deletion of your account (subject to legal retention requirements)
        • Opt out of promotional communications
        • Receive a copy of your data in a portable format
        Contact our support team to exercise these rights.`,
    },
    {
      title: "Data Retention",
      icon: FaDatabase,
      content: `We retain your personal information for as long as necessary to:
        • Provide our services to you
        • Comply with legal obligations
        • Resolve disputes and enforce agreements
        Transaction records may be retained for up to 7 years as required by financial regulations.`,
    },
    {
      title: "Children's Privacy",
      icon: FaLock,
      content: `Heads or Tails ${COUNTRY} is strictly for users 18 years and older. We do not knowingly collect information from minors. If we discover that a minor has created an account, we will immediately suspend it and delete associated data.`,
    },
    {
      title: "Changes to This Policy",
      icon: FaEye,
      content: `We may update this Privacy Policy from time to time. We will notify you of significant changes via SMS or email. Your continued use of our platform after changes constitutes acceptance of the updated policy.`,
    },
    {
      title: "Contact Us",
      icon: FaEnvelope,
      content: `For privacy-related questions or concerns, contact us at:
      
        • Phone: ${PHONENUMBER}
        • Address:${CAPITAL}, ${COUNTRY}
        `,
    },
    //  • Email: privacy@Heads or Tails{COUNTRY}.com
    /** */
    // We aim to respond to all inquiries within 48 hours.
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#071129] to-[#0a1628] py-8 px-4">
      <motion.div
        className="max-w-4xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Back Button */}
        <motion.div variants={itemVariants} className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-blue-400 transition-colors duration-300"
          >
            <FaArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 mb-4">
            <FaLock className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-500 bg-clip-text text-transparent mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Your privacy matters to us. This policy explains how we collect,
            use, and protect your information. Last updated: January 2026
          </p>
        </motion.div>

        {/* Privacy Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-blue-500/30 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white mb-3">
                    {section.title}
                  </h2>
                  <p className="text-gray-400 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer Note */}
        <motion.div
          variants={itemVariants}
          className="mt-12 text-center text-gray-500 text-sm"
        >
          <p>
            By using Heads or Tails {COUNTRY}, you agree to this Privacy Policy.
          </p>
          {/* <p className="mt-2">
            Questions? Contact us at{" "}
            <a
              href="mailto:privacy@Heads or Tails{COUNTRY}.com"
              className="text-blue-400 hover:text-blue-300 transition-colors"
            >
              privacy@Heads or Tails{COUNTRY}.com
            </a>
          </p> */}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PrivacyPolicy;
